// frontend/src/components/plot/charts/chartUtils.js
export const METRIC_PANEL_ORDER = {
  crop: [
    'crop:degree_days',
    'crop:degree_hours',
    'pest:degree_days',
    'pest:degree_hours',
    'friend:degree_days',
    'friend:degree_hours'
  ],
  realtime: [
    'vpd',
    'lwd',
    'fuel_moisture',
    'heatindex'
  ],
  weather: [
    'temperature',
    'dewpoint',
    'humidity',
    'pressure',
    'precipitation',
    'irrad',
    'wind'
  ],
  telemetry: [
    'battery',
    'battery_voltage',
    'rssi',
    'wifi',
    'disk',
    'disk_free',
    'disk_free_percent'
  ],
  custom: []
};

export const sortCategoryMetrics = (categoryKey, metrics = []) => {
  const normCategory = String(categoryKey).toLowerCase();
  const categoryOrderKey = Object.keys(METRIC_PANEL_ORDER).find((key) => normCategory.includes(key));
  const preferredOrder = METRIC_PANEL_ORDER[categoryOrderKey] || [];

  return [...metrics].sort((a, b) => {
    const keyA = typeof a === 'string' ? a : (a.type || a.id || '');
    const keyB = typeof b === 'string' ? b : (b.type || b.id || '');

    const getRank = (key) => {
      const exactIdx = preferredOrder.indexOf(key);
      if (exactIdx !== -1) return exactIdx;
      const prefixIdx = preferredOrder.findIndex((prefix) => key.startsWith(prefix));
      if (prefixIdx !== -1) return prefixIdx;
      return 999;
    };

    const rankA = getRank(keyA);
    const rankB = getRank(keyB);

    if (rankA !== rankB) return rankA - rankB;
    return keyA.localeCompare(keyB);
  });
};

const METRIC_AXIS_CONFIG = {
  pressure: { mode: 'tight', deltaPadding: 2 },
  humidity: { mode: 'fixed', min: 0, max: 100 },
  fuel_moisture: { mode: 'fixed', min: 0, max: 100 },
  precip: { mode: 'zero_based', percentPadding: 0.2 },
  precipitation: { mode: 'zero_based', percentPadding: 0.2 },
  irrad: { mode: 'zero_based', percentPadding: 0.15 },
  wind: { mode: 'zero_based', percentPadding: 0.15 },
  temp: { mode: 'padded', percentPadding: 0.1 },
  vpd: { mode: 'zero_based', percentPadding: 0.1 },
  battery: { mode: 'padded', percentPadding: 0.1 },
  battery_voltage: { mode: 'padded', percentPadding: 0.1 },
  rssi: { mode: 'tight', deltaPadding: 5 },
  wifi: { mode: 'tight', deltaPadding: 5 },
  disk: { mode: 'fixed', min: 0, max: 100 },
  disk_free_percent: { mode: 'fixed', min: 0, max: 100 },
  default: { mode: 'padded', percentPadding: 0.05 }
};

export const getYAxisDomain = (metricId) => {
  const config = METRIC_AXIS_CONFIG[metricId] || METRIC_AXIS_CONFIG.default;

  switch (config.mode) {
    case 'fixed':
      return [config.min, config.max];
    case 'zero_based':
      return [
        0,
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          const pad = Math.abs(dataMax) * (config.percentPadding || 0.1);
          return Math.ceil(dataMax + pad);
        }
      ];
    case 'tight':
      return [
        (dataMin) => {
          if (!Number.isFinite(dataMin)) return 'auto';
          return Math.floor(dataMin - (config.deltaPadding || 2));
        },
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          return Math.ceil(dataMax + (config.deltaPadding || 2));
        }
      ];
    case 'padded':
    default:
      return [
        (dataMin) => {
          if (!Number.isFinite(dataMin)) return 'auto';
          const pad = Math.abs(dataMin) * (config.percentPadding || 0.05) || 1;
          return Math.floor(dataMin - pad);
        },
        (dataMax) => {
          if (!Number.isFinite(dataMax)) return 'auto';
          const pad = Math.abs(dataMax) * (config.percentPadding || 0.05) || 1;
          return Math.ceil(dataMax + pad);
        }
      ];
  }
};

export function computeWindSpeedAndDirection(u, v) {
  if (u === null || u === undefined || v === null || v === undefined) {
    return { speed: null, direction: null };
  }
  const speed = Math.sqrt(u * u + v * v);
  let dirDeg = Math.atan2(-u, -v) * (180 / Math.PI);
  if (dirDeg < 0) dirDeg += 360;

  return {
    speed: Number(speed.toFixed(1)),
    direction: Number(dirDeg.toFixed(0))
  };
}

export function getWindCompassCardinals(deg) {
  if (deg === null || deg === undefined || isNaN(deg)) return '';
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const val = Math.floor((deg / 22.5) + 0.5);
  return directions[(val % 16)];
}

export function getGrowthStageKey(chartData, metricKey) {
  if (!Array.isArray(chartData) || chartData.length === 0 || !metricKey) return null;
  if (!metricKey.includes('degree_days') && !metricKey.includes('degree_hours')) return null;

  const targetBase = metricKey
    .replace('degree_days', 'growth_stage')
    .replace('degree_hours', 'growth_stage');

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (record && targetBase in record && record[targetBase] !== undefined) {
      return targetBase;
    }
  }

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (!record) continue;
    for (const key in record) {
      if (key.startsWith(targetBase) && record[key] !== undefined) {
        return key;
      }
    }
  }

  for (let i = 0; i < chartData.length; i++) {
    const record = chartData[i];
    if (!record) continue;
    const fallbackKey = Object.keys(record).find((k) => k.includes('growth_stage') && record[k] !== undefined);
    if (fallbackKey) return fallbackKey;
  }

  return null;
}

export function computeStageTransitions(chartData, stageKey) {
  if (!Array.isArray(chartData) || chartData.length === 0 || !stageKey) return [];

  const transitions = [];
  let lastStage = null;

  chartData.forEach((point) => {
    const stageValRaw = point[stageKey];
    if (stageValRaw !== undefined && stageValRaw !== null) {
      const stageVal = typeof stageValRaw === 'object'
        ? String(stageValRaw.name || stageValRaw.label || stageValRaw.value || stageValRaw.id || JSON.stringify(stageValRaw))
        : String(stageValRaw);

      if (stageVal !== lastStage) {
        transitions.push({
          x: point.timestamp,
          stage: stageVal,
          label: isNaN(Number(stageVal)) ? stageVal : `Stage ${stageVal}`
        });
        lastStage = stageVal;
      }
    }
  });

  return transitions;
}

export const getFilteredChartData = (chartData, forecastVisible) => {
  if (!forecastVisible && Array.isArray(chartData) && chartData.length > 0) {
    let maxHistTimestamp = 0;
    chartData.forEach((pt) => {
      const hasHist = Object.keys(pt).some((k) => k.endsWith('_hist') && pt[k] !== null && pt[k] !== undefined);
      if (hasHist && pt.timestamp > maxHistTimestamp) {
        maxHistTimestamp = pt.timestamp;
      }
    });
    if (maxHistTimestamp > 0) {
      return chartData.filter((pt) => pt.timestamp <= maxHistTimestamp);
    }
  }
  return chartData;
};

export const formatXAxisTick = (ts, localTimeEnabled = true) => {
  if (!ts) return '';
  if (typeof ts === 'string') return ts;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return String(ts);

  const m = date.getMonth() + 1;
  const d = date.getDate();
  const hh = String(localTimeEnabled ? date.getHours() : date.getUTCHours()).padStart(2, '0');
  return `${m}/${d} ${hh}:00`;
};

export function groupAccumulationMetricsBySpecies(metrics = []) {
  const groups = {};
  metrics.forEach((m) => {
    if (m.type.includes('growth_stage')) return;
    let modelKey = 'default';
    if (m.type.includes('_')) {
      const parts = m.type.split('_');
      modelKey = parts[parts.length - 1];
      if (modelKey === 'days' || modelKey === 'hours' || modelKey === 'degree') {
        modelKey = 'default';
      }
    }
    if (!groups[modelKey]) {
      groups[modelKey] = {
        name: modelKey === 'default' ? 'Accumulation & Phenology' : modelKey.replace(/_/g, ' ').toUpperCase(),
        metrics: []
      };
    }
    groups[modelKey].metrics.push(m);
  });
  return Object.values(groups);
}
